import { BaseElement } from '../base-element';

type SectionCardVariant = 'default' | 'error' | 'success';

export class DtSectionCard extends BaseElement {
  static observedAttributes = ['icon', 'heading', 'error-count', 'complete', 'variant'];

  private _icon = '';
  private _heading = '';
  private _errorCount = 0;
  private _complete = false;
  private _variant: SectionCardVariant = 'default';
  private _built = false;
  private readonly _headingId = `dt-section-card-heading-${Math.random().toString(36).slice(2, 9)}`;

  private _sectionEl: HTMLElement | null = null;
  private _headerEl: HTMLElement | null = null;
  private _iconWrapEl: HTMLElement | null = null;
  private _iconEl: HTMLElement | null = null;
  private _headingEl: HTMLElement | null = null;
  private _statusEl: HTMLElement | null = null;

  get icon(): string {
    return this._icon;
  }

  set icon(value: string) {
    this._icon = value?.trim() ?? '';
    this.syncDom();
  }

  get heading(): string {
    return this._heading;
  }

  set heading(value: string) {
    this._heading = value?.trim() ?? '';
    this.syncDom();
  }

  get errorCount(): number {
    return this._errorCount;
  }

  set errorCount(value: number) {
    this._errorCount = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
    this.syncDom();
  }

  get complete(): boolean {
    return this._complete;
  }

  set complete(value: boolean) {
    this._complete = Boolean(value);
    this.syncDom();
  }

  get variant(): SectionCardVariant {
    return this._variant;
  }

  set variant(value: string) {
    if (value === 'default' || value === 'error' || value === 'success') {
      this._variant = value;
      this.syncDom();
    }
  }

  connectedCallback(): void {
    if (!this._built) {
      this.buildCard();
    }

    this.syncDom();
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    switch (name) {
      case 'icon':
        this.icon = newValue ?? '';
        break;
      case 'heading':
        this.heading = newValue ?? '';
        break;
      case 'error-count':
        this.errorCount = this.parseErrorCount(newValue);
        break;
      case 'complete':
        this.complete = newValue !== null && newValue !== 'false';
        break;
      case 'variant':
        this.variant = newValue ?? 'default';
        break;
    }
  }

  private parseErrorCount(value: string | null): number {
    if (!value) {
      return 0;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  private buildCard(): void {
    const bodyFragment = document.createDocumentFragment();

    for (const node of Array.from(this.childNodes)) {
      if (node.nodeType === Node.ELEMENT_NODE || node.textContent?.trim()) {
        bodyFragment.appendChild(node);
      }
    }

    this.textContent = '';

    this._sectionEl = document.createElement('section');
    this._sectionEl.className = this.getSectionClasses();
    this._sectionEl.setAttribute('aria-labelledby', this._headingId);

    this._headerEl = document.createElement('div');
    this._headerEl.className = this.getHeaderClasses();

    this._iconWrapEl = document.createElement('div');
    this._iconWrapEl.className = this.getIconWrapClasses();
    this._iconEl = document.createElement('span');
    this._iconEl.className = 'material-symbols-outlined section-card-icon-symbol text-lg sm:text-xl';
    this._iconEl.setAttribute('aria-hidden', 'true');
    this._iconWrapEl.appendChild(this._iconEl);

    this._headingEl = document.createElement('h2');
    this._headingEl.id = this._headingId;
    this._headingEl.className = 'section-card-heading text-base font-semibold text-neutral-900 sm:text-lg';

    this._statusEl = document.createElement('span');
    this._statusEl.className = this.getStatusClasses();
    this._statusEl.setAttribute('role', 'status');
    this._statusEl.setAttribute('aria-live', 'polite');

    this._headerEl.append(this._iconWrapEl, this._headingEl, this._statusEl);

    const bodyEl = document.createElement('div');
    bodyEl.className = 'section-card-body p-6';
    bodyEl.appendChild(bodyFragment);

    this._sectionEl.append(this._headerEl, bodyEl);
    this.appendChild(this._sectionEl);
    this._built = true;
  }

  private getResolvedVariant(): SectionCardVariant {
    if (this._errorCount > 0 || this._variant === 'error') {
      return 'error';
    }

    if (this._complete || this._variant === 'success') {
      return 'success';
    }

    return 'default';
  }

  private getSectionClasses(): string {
    const base = 'section-card overflow-hidden rounded-xl border bg-white shadow-xs';
    const resolvedVariant = this.getResolvedVariant();

    if (resolvedVariant === 'error') {
      return `${base} border-error-500`;
    }

    if (resolvedVariant === 'success') {
      return `${base} border-success-500`;
    }

    return `${base} border-neutral-100`;
  }

  private getHeaderClasses(): string {
    const base =
      'section-card-header flex items-center gap-2 border-b px-4 py-3 sm:gap-3 sm:px-6 sm:py-4';
    const resolvedVariant = this.getResolvedVariant();

    if (resolvedVariant === 'error') {
      return `${base} border-error-100 bg-error-50/30`;
    }

    if (resolvedVariant === 'success') {
      return `${base} border-success-100 bg-success-50/30`;
    }

    return `${base} border-neutral-100`;
  }

  private getIconWrapClasses(): string {
    const base = 'section-card-icon flex items-center justify-center rounded-lg p-1.5 sm:p-2';
    const resolvedVariant = this.getResolvedVariant();

    if (resolvedVariant === 'error') {
      return `${base} bg-error-100 text-error-500`;
    }

    if (resolvedVariant === 'success') {
      return `${base} bg-success-100 text-success-600`;
    }

    return `${base} bg-primary-50 text-primary-600`;
  }

  private getStatusClasses(): string {
    const base =
      'section-card-status ml-auto inline-flex items-center gap-1 text-xs font-medium';

    return this.getResolvedVariant() === 'error'
      ? `${base} text-error-600`
      : `${base} text-success-600`;
  }

  private getStatusMarkup(): string {
    if (this._errorCount > 0) {
      const label = this._errorCount === 1 ? '1 error' : `${this._errorCount} errors`;
      return `
        <span class="material-symbols-outlined text-sm" aria-hidden="true">error</span>
        ${this.esc(label)}
      `;
    }

    if (this._complete) {
      return `
        <span class="material-symbols-outlined text-sm" aria-hidden="true">check_circle</span>
        Complete
      `;
    }

    return '';
  }

  private syncDom(): void {
    if (!this._built || !this._sectionEl || !this._headerEl || !this._iconWrapEl || !this._iconEl || !this._headingEl || !this._statusEl) {
      return;
    }

    this._sectionEl.className = this.getSectionClasses();
    this._headerEl.className = this.getHeaderClasses();
    this._iconWrapEl.className = this.getIconWrapClasses();

    this._iconEl.textContent = this._icon;
    this._iconWrapEl.hidden = this._icon.length === 0;

    this._headingEl.textContent = this._heading;

    this._statusEl.className = this.getStatusClasses();
    this._statusEl.innerHTML = this.getStatusMarkup();
    this._statusEl.hidden = this._statusEl.innerHTML.trim().length === 0;
  }
}

customElements.define('dt-section-card', DtSectionCard);